import * as React from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled, useTheme } from "@mui/material/styles";
import { mangoFusionPalette } from "@mui/x-charts/colorPalettes";
import { useTranslation } from "react-i18next";

const size = {
  width: 230,
  height: 200,
};

const StyledText = styled("text")(({ theme }) => ({
  fill: "#193666",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 12,
}));

const formatNumber = (num) => {
  if (num % 1 !== 0) {
    return num.toFixed(2);
  }
  return num.toFixed(0);
};

const CustomLegend = ({ data, total, unit, isZoomed }) => {
  return (
    <div style={{ marginLeft: "5px", paddingRight: "15px" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isZoomed ? "200px 50px 50px" : "150px 30px 30px",
          gap: "12px",
          borderBottom: "1px solid #D3D3D3",
          padding: "8px 0",
          fontSize: "12px",
          color: "#193666",
        }}
      >
        <div>Label</div>
        <div style={{ textAlign: "right" }}>{unit}</div>
        <div style={{ textAlign: "right" }}>%</div>
      </div>
      {data.map((item, index) => (
        <div
          key={index}
          style={{
            display: "grid",
            gridTemplateColumns: isZoomed
              ? "200px 50px 50px"
              : "150px 30px 30px",
            gap: "12px",
            padding: "8px 0",
            fontSize: "12px",
            color: "#193666",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                minWidth: "12px",
                width: "12px",
                height: "12px",
                flexShrink: 0,
                backgroundColor: item.color,
                marginRight: "8px",
                borderRadius: "50%",
              }}
            />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {item.label}
            </span>
          </div>
          <div style={{ textAlign: "right", textWrap: "wrap" }}>
            {item.value && formatNumber(item.value)}
          </div>
          <div style={{ textAlign: "right" }}>
            {((item.value / total) * 100).toFixed(0)}
          </div>
        </div>
      ))}
    </div>
  );
};

function PieCenterLabel({ centerText, total }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <g>
      <StyledText
        x={left + width / 2}
        y={top + height / 2 - 10}
        style={{ fontSize: "12px" }}
      >
        {centerText}
      </StyledText>
      <StyledText
        x={left + width / 2}
        y={top + height / 2 + 10}
        style={{ fontSize: "20px", fontWeight: "bold" }}
      >
        {total}
      </StyledText>
    </g>
  );
}

const NoDataDisplay = () => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        width: "100%",
        textAlign: "center",
        color: "#193666",
        fontSize: "14px",
        marginTop: "80px", // Adjust this value to control the vertical position
      }}
    >
      {t("NO_DATA_TO_DISPLAY")}
    </div>
  );
};

export default function PieChartWithCenterLabel({
  centerText,
  data,
  slicedData,
  show,
  unit,
  isZoomed,
}) {
  const theme = useTheme();
  const palette = React.useMemo(() => {
    // Get the appropriate palette based on the theme mode
    return mangoFusionPalette(theme.palette.mode);
  }, [theme.palette.mode]);
  const total = React.useMemo(
    () => data.reduce((acc, item) => acc + (item.value || 0), 0),
    [data]
  );

  const half = Math.ceil(data.length / 2); // Get the midpoint
  const firstHalf = data.slice(0, half); // First half
  const secondHalf = data.slice(half);

  const formattedTotal = total ? formatNumber(total) : 0;

  const coloredData = React.useMemo(() => {
    return data.map((item, index) => ({
      ...item,
      value: item.value,
      label: item.label,
      id: index,
    }));
  }, [data]);

  const coloredDataLegend = React.useMemo(() => {
    const sourceData = slicedData && !show && !isZoomed ? slicedData : data;
    return sourceData.map((item, index) => ({
      ...item,
      color: palette[index % palette.length],
    }));
  }, [data, slicedData, show, isZoomed, palette]);

  const coloredFirstHalf = React.useMemo(() => {
    return firstHalf.map((item, index) => ({
      ...item,
      color: palette[index % palette.length],
    }));
  }, [firstHalf, palette]);

  const coloredSecondHalf = React.useMemo(() => {
    return secondHalf.map((item, index) => ({
      ...item,
      color: palette[(index + firstHalf.length) % palette.length],
    }));
  }, [secondHalf, firstHalf, palette]);

  // Check if there's no data or if total is 0
  if (!data?.length || total === 0) {
    return <NoDataDisplay />;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isZoomed ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        maxWidth: isZoomed ? "800px" : "500px",
      }}
    >
      <PieChart
        series={[
          {
            data: coloredData,
            innerRadius: 80,
            outerRadius: 100,
            highlightScope: { faded: "global", highlighted: "item" },
            faded: { innerRadius: 30, additionalRadius: -30 },
            palette: palette,
          },
        ]}
        {...size}
        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
        slotProps={{ legend: { hidden: true } }}
      >
        <PieCenterLabel centerText={centerText} total={formattedTotal} />
      </PieChart>

      {!isZoomed ? (
        <CustomLegend
          data={coloredDataLegend}
          total={formattedTotal}
          unit={unit}
          isZoomed={isZoomed}
        />
      ) : (
        <div style={{ display: "flex" }}>
          <CustomLegend
            data={coloredFirstHalf}
            total={formattedTotal}
            unit={unit}
            isZoomed={isZoomed}
          />
          <CustomLegend
            data={coloredSecondHalf}
            total={formattedTotal}
            unit={unit}
            isZoomed={isZoomed}
          />
        </div>
      )}
    </div>
  );
}
